import React, { useState, useEffect } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView } from '../analytics';

const SplitRemoveRotatePdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processingPdf, setProcessingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [pageRotations, setPageRotations] = useState({});
  const [pagesToDelete, setPagesToDelete] = useState([]);
  const [processedPdfUrl, setProcessedPdfUrl] = useState(null);
  
  // Handle file selection
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validate that the selected file is a PDF
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      
      // Send the file to the API to get thumbnails
      await sendFileForThumbnails(file);
    }
  };
  
  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname);
    }
  }, []);
  
  // Handle page rotation
  const handleRotate = (pageIndex, direction) => {
    setPageRotations(prev => {
      const currentRotation = prev[pageIndex] || 0;
      // Add or subtract 90 degrees, keeping within 0-270 range
      const newRotation = (currentRotation + (direction === 'right' ? 90 : -90) + 360) % 360;
      return { ...prev, [pageIndex]: newRotation };
    });
  };
  
  // Handle page deletion
  const handleDelete = (pageIndex) => {
    setPagesToDelete(prev => {
      // Toggle deletion state
      if (prev.includes(pageIndex)) {
        return prev.filter(index => index !== pageIndex);
      } else {
        return [...prev, pageIndex];
      }
    });
  };
  
  // Process PDF with all modifications
  const processPdf = async () => {
    if (!selectedFile) {
      setError('No PDF file selected');
      return;
    }
    
    setProcessingPdf(true);
    setError(null);
    setProcessedPdfUrl(null);
    
    try {
      // Create the page order array (excluding deleted pages) - only page numbers as integers
      const pageOrderArray = [];
      
      // Create rotation angles object in the format {"1": 90, "3": 180}
      const rotationAngles = {};
      
      for (let i = 0; i < thumbnails.length; i++) {
        if (!pagesToDelete.includes(i)) {
          // Add the page number (1-indexed) to the array
          const pageNumber = i + 1; // 1-indexed page numbers for the API
          pageOrderArray.push(pageNumber);
          
          // If this page has rotation, add it to the rotation angles object
          const rotation = pageRotations[i];
          if (rotation && rotation !== 0) {
            rotationAngles[pageNumber.toString()] = rotation;
          }
        }
      }
      
      // Convert the arrays/objects to JSON strings
      const pageOrderJson = JSON.stringify(pageOrderArray);
      const rotationAnglesJson = JSON.stringify(rotationAngles);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('page_order', pageOrderJson);
      
      // Only include rotation_angles if there are actually rotated pages
      if (Object.keys(rotationAngles).length > 0) {
        formData.append('rotation_angles', rotationAnglesJson);
        console.log('Sending request with rotation_angles:', rotationAnglesJson);
      }
      
      console.log('Sending request with page_order:', pageOrderJson);
      
      const response = await fetch('https://api.temp2doc.com/utilities/organize', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      // Handle the response - it should be a modified PDF file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setProcessedPdfUrl(url);
      
      // Optional: Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `modified_${selectedFile.name}`;
      a.click();
      
    } catch (err) {
      setError(`Failed to process PDF: ${err.message}`);
      console.error('Error processing PDF:', err);
    } finally {
      setProcessingPdf(false);
    }
  };
  
  // Send file to the API
  const sendFileForThumbnails = async (file) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('return_type', 'base64');
      formData.append('format', 'png');
      formData.append('width', '200');
      
      const response = await fetch('https://api.temp2doc.com/utilities/thumbnails', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Log the response to understand its structure
      console.log('API Response:', data);
      
      // Direct access to the thumbnails array if it exists
      if (data && data.thumbnails) {
        // Handle different response structures
        if (Array.isArray(data.thumbnails)) {
          const processedThumbnails = data.thumbnails.map(thumbnail => {
            let base64Data = '';
            
            // Direct access to string value
            if (typeof thumbnail === 'string') {
              base64Data = thumbnail;
            }
            // Handle object with image data
            else if (thumbnail && typeof thumbnail === 'object') {
              // Try common property names for base64 data
              if (thumbnail.base64) {
                base64Data = thumbnail.base64;
              } else if (thumbnail.data) {
                base64Data = thumbnail.data;
              } else if (thumbnail.image) {
                base64Data = thumbnail.image;
              } else if (thumbnail.content) {
                base64Data = thumbnail.content;
              } else {
                // Last resort: try to find any string property that might contain base64 data
                for (const key in thumbnail) {
                  if (typeof thumbnail[key] === 'string' && thumbnail[key].length > 100) {
                    console.log(`Found potential base64 data in property: ${key}`);
                    base64Data = thumbnail[key];
                    break;
                  }
                }
                
                if (!base64Data) {
                  // If all else fails, log the object for debugging
                  console.warn('Could not extract base64 data from thumbnail:', thumbnail);
                  return '';
                }
              }
            } else {
              // Fallback for any other case
              return '';
            }
            
            // Remove any existing data:image/png;base64, prefix to avoid duplicates
            const prefix = 'data:image/png;base64,';
            if (base64Data.startsWith(prefix)) {
              return base64Data;
            } else {
              // Remove any other potential prefixes that might be there
              const prefixRegex = /^data:[^;]+;base64,/;
              if (prefixRegex.test(base64Data)) {
                base64Data = base64Data.replace(prefixRegex, '');
              }
              return base64Data;
            }
          }).filter(base64 => base64.length > 0); // Remove empty strings
          
          setThumbnails(processedThumbnails);
        } else if (typeof data.thumbnails === 'object' && !Array.isArray(data.thumbnails)) {
          // Handle case where thumbnails is an object with numbered keys
          const thumbnailArray = [];
          for (const key in data.thumbnails) {
            if (data.thumbnails.hasOwnProperty(key)) {
              const value = data.thumbnails[key];
              if (typeof value === 'string') {
                thumbnailArray.push(value);
              } else if (typeof value === 'object') {
                // Try to extract base64 data from object
                const base64Data = value.base64 || value.data || value.image || value.content || '';
                if (base64Data) {
                  thumbnailArray.push(base64Data);
                }
              }
            }
          }
          setThumbnails(thumbnailArray);
        } else {
          console.warn('Unexpected thumbnails format:', data.thumbnails);
          setThumbnails([]);
        }
      } else {
        console.warn('No thumbnails found in response:', data);
        setThumbnails([]);
      }
    } catch (err) {
      setError(`Failed to process PDF: ${err.message}`);
      console.error('Error processing PDF:', err);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <SEOMetaTags
        title="Split, Remove & Rotate PDF Pages | Free PDF Editor Online"
        description="Edit your PDF documents online for free. Split PDFs into multiple files, remove specific pages, or rotate pages with our easy-to-use PDF editing tools."
        keywords="split pdf, remove pdf pages, rotate pdf pages, pdf editor, free pdf tools, online pdf editor"
        canonicalUrl="https://temp2doc.com/split-remove-rotate-pdf"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Split, Remove & Rotate PDF Pages",
          "applicationCategory": "WebApplication",
          "operatingSystem": "Any",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Edit your PDF documents online for free. Split PDFs into multiple files, remove specific pages, or rotate pages with our easy-to-use PDF editing tools."
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Split, Remove & Rotate PDF Pages</h1>
          <p className="text-xl text-gray-600 mb-8">
            Easily manipulate your PDF documents with our free PDF editing tools.
          </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-primary-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload PDF File</h2>
          <p className="text-gray-600 text-center max-w-md mb-6">
            Select a PDF file to edit. You'll be able to split it into multiple files, remove specific pages, or rotate pages.
          </p>
          <div className="w-full max-w-md">
            <label className="flex flex-col items-center px-4 py-6 bg-white text-primary-600 rounded-lg shadow-md tracking-wide border border-primary-300 cursor-pointer hover:bg-gray-50 transition-colors" role="button" aria-label="Select PDF file to edit">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="mt-2 text-base leading-normal">Select PDF Files</span>
              <input 
                type='file' 
                className="hidden" 
                accept=".pdf" 
                onChange={handleFileChange}
                aria-label="Upload PDF file"
              />
            </label>
            {error && (
              <div className="mt-3 text-red-500 text-sm" role="alert" aria-live="assertive">{error}</div>
            )}
            {selectedFile && (
              <div className="mt-3 text-green-500 text-sm" aria-live="polite">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>
        </div>
          </div>
        </section>

        {/* PDF Preview Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">PDF Preview</h2>
            <div className="border border-gray-200 rounded-lg min-h-96 flex items-center justify-center bg-gray-50 p-4" role="region" aria-label="PDF preview area">
              {isLoading ? (
                <div className="text-center" aria-live="polite">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mb-4" aria-hidden="true"></div>
                  <p className="text-gray-500">Processing PDF...</p>
                </div>
              ) : thumbnails.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
              {thumbnails.map((thumbnail, index) => (
                <div key={index} className={`border rounded-lg overflow-hidden shadow-sm transition-opacity duration-300 ${pagesToDelete.includes(index) ? 'opacity-50 border-red-500' : 'border-gray-200'}`}>
                  <div className="relative pb-[140%] bg-white">
                    <img 
                      src={thumbnail.startsWith('data:image/png;base64,') ? thumbnail : `data:image/png;base64,${thumbnail}`} 
                      alt={`Page ${index + 1}`} 
                      className="absolute inset-0 w-full h-full object-contain"
                      style={{ transform: `rotate(${pageRotations[index] || 0}deg)`, transition: 'transform 0.3s ease' }}
                    />
                    
                    {/* Action buttons overlay */}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 p-2 bg-black bg-opacity-50 transition-opacity opacity-0 hover:opacity-100 group-hover:opacity-100">
                      {/* Rotate Left Button */}
                      <button 
                        onClick={() => handleRotate(index, 'left')} 
                        className="p-1 rounded-full bg-white text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Rotate Left"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>
                      
                      {/* Rotate Right Button */}
                      <button 
                        onClick={() => handleRotate(index, 'right')} 
                        className="p-1 rounded-full bg-white text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Rotate Right"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                      
                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDelete(index)} 
                        className={`p-1 ${pagesToDelete.includes(index) ? 'bg-red-500 text-white' : 'bg-white text-gray-800'} hover:bg-red-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-red-500`}
                        title={pagesToDelete.includes(index) ? 'Undo Delete' : 'Delete Page'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 p-2 flex justify-between items-center text-sm">
                    <span className="font-medium">Page {index + 1}</span>
                    <div className="flex gap-1">
                      {/* Small action buttons for better visibility */}
                      <button 
                        onClick={() => handleRotate(index, 'left')} 
                        className="p-1 text-gray-600 hover:text-primary-600 focus:outline-none"
                        title="Rotate Left"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleRotate(index, 'right')} 
                        className="p-1 text-gray-600 hover:text-primary-600 focus:outline-none"
                        title="Rotate Right"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => handleDelete(index)} 
                        className={`p-1 ${pagesToDelete.includes(index) ? 'text-red-500' : 'text-gray-600'} hover:text-red-600 focus:outline-none`}
                        title={pagesToDelete.includes(index) ? 'Undo Delete' : 'Delete Page'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              ) : selectedFile ? (
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <p className="mt-4 text-gray-500">PDF loaded. Generating preview...</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  <p className="mt-4 text-gray-500">Upload a PDF to preview and edit</p>
                </div>
              )}
            </div>
          </div>
        </section>
      
        {/* Process PDF Button Section */}
        {thumbnails.length > 0 && (
          <div className="bg-white rounded-lg shadow-xl p-8 border border-gray-200 mb-12 max-w-6xl mx-auto">
            <div className="flex flex-col items-center justify-center">
            <div className="w-full max-w-md">
              <div className="mb-4 text-center">
                <p className="text-gray-600">
                  {pagesToDelete.length > 0 ? (
                    <span className="font-medium">{pagesToDelete.length} page(s) marked for deletion.</span>
                  ) : ''}
                  {Object.keys(pageRotations).length > 0 ? (
                    <span className="font-medium">{Object.keys(pageRotations).length} page(s) rotated.</span>
                  ) : ''}
                </p>
              </div>
              
              <button
                onClick={processPdf}
                disabled={processingPdf || !selectedFile}
                className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-colors ${processingPdf ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
              >
                {processingPdf ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing PDF...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Split Remove Rotate PDF
                  </>
                )}
              </button>
              
              {error && (
                <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
              
              {processedPdfUrl && (
                <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-lg">
                  <p className="mb-2">PDF successfully processed!</p>
                  <a 
                    href={processedPdfUrl} 
                    download={`modified_${selectedFile?.name}`}
                    className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Download Modified PDF
                  </a>
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Split PDF</h3>
              <p className="text-gray-600">
                Extract specific page ranges into a new PDF document. Select the pages you want to keep in your new file.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Remove Pages</h3>
              <p className="text-gray-600">
                Delete unwanted pages from your PDF document. Simply mark the pages you want to remove.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Rotate Pages</h3>
              <p className="text-gray-600">
                Change the orientation of specific pages. Rotate pages 90°, 180°, or 270° to correct their alignment.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Multi-Function Tool</h3>
              <p className="text-gray-600 text-sm">
                Split, remove, and rotate PDF pages all in one convenient tool.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Visual Preview</h3>
              <p className="text-gray-600 text-sm">
                See thumbnail previews of all pages before making changes.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Browser-Based</h3>
              <p className="text-gray-600 text-sm">
                All operations are processed in your browser for complete privacy.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200" aria-label="Related Tools">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <li>
                <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Chat PDF With AI</span>
                </a>
              </li>
              <li>
                <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Merge PDF</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">PDF to Word</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Excel</span>
            </a>
          </li>
              <li>
                <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Image</span>
            </a>
          </li>
              <li>
                <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
            </a>
          </li>
              <li>
                <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Protect PDF</span>
            </a>
          </li>
              <li>
                <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Unlock PDF</span>
            </a>
          </li>
              <li>
                <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
            </a>
          </li>
              <li>
                <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Compress PDF</span>
            </a>
          </li>
              <li>
                <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
            </a>
          </li>
              <li>
                <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Back to Home</span>
            </a>
          </li>
            </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default SplitRemoveRotatePdf;
